import {createRecaptchaLoader} from '~/utils/recaptchaLoader';

// One loader for the whole app: Google's script is fetched at most once, and only when a page
// that sends a reCAPTCHA answer asks for it (recaptchaLoader). Nothing loads it for the app as a
// whole, so a page that never asks (the anonymous feedback page, any ordinary page) makes no
// request to Google.
const loader = createRecaptchaLoader(() => import('recaptcha-v3'));
const currentPath = () => (typeof window === 'undefined' ? null : window.location.pathname);

/**
 * The exported executeRecaptcha function allows
 * you to execute reCAPTCHA actions
 * and retrieve the reCAPTCHA token along with the header options
 * to be used in subsequent requests.
 *
 * preloadRecaptcha starts loading the script when a page that will need it opens, so reCAPTCHA
 * sees the page before the answer is asked for, as it did when it loaded with the app.
 */
export function useGoogleRecaptcha (){
  const siteKey = useRuntimeConfig().public.googleRecaptchaSiteKey
  const preloadRecaptcha = () => {
    if (typeof window === 'undefined') return;
    loader.load(siteKey, currentPath()).catch(() => {
      // Tried again when the answer is asked for.
    });
  }
  const executeRecaptcha = async action => {
    // No site key configured: there is no token to be had, so ask for none.
    //
    // This matters because of what the API does with the answer. It skips
    // verification entirely when no token arrives outside release mode, but
    // verifies whatever DOES arrive — so a local environment sending a token
    // minted from one key pair, to a server holding another, fails every
    // sign-up with "Please prove you are not a robot" and no way to get past
    // it. Sending nothing is the honest signal, and the one the server
    // already knows how to read.
    if (!siteKey) return {token: null, headerOptions: {headers: {}}}

    const instance = await loader.load(siteKey, currentPath())
    const token = instance ? await instance.execute(action) : null
    const headerOptions = {
      headers: {
        'google-recaptcha-token': token
      }
    }
    return { token, headerOptions }
  }
  return { executeRecaptcha, preloadRecaptcha }
}
